// EditorDrawer — thin wrapper around `Drawer` that requires a `dirty: boolean`
// prop and auto-wires `useUnsavedChangesGuard` so future entity editors cannot
// forget to register their dirty tracking.
//
// @aiPurpose Architecturally enforce the unsaved-changes guard on every
//   editable drawer surface by making `dirty` a required prop and routing
//   the underlying `Drawer.onClose` through `requestClose`.
// @aiInvariant Composes the existing `Drawer` primitive; does NOT duplicate
//   its implementation. The hook owns close-path arbitration; the wrapper
//   merely funnels props.
import { useMemo, type ReactNode } from 'react';
import { Drawer, type DrawerProps } from './Drawer.js';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard.js';
import { EditorGuardContext, type EditorGuardValue } from './useEditorGuard.js';

export interface EditorDrawerProps extends Omit<DrawerProps, 'onClose' | 'dismissDisabled'> {
  /** True when the surface has meaningful unsaved changes vs its baseline. */
  dirty: boolean;
  /** True while a save/create/reset/delete/upload mutation owned by the surface is in flight. */
  busy?: boolean;
  /** Called when the user confirms discard or when the surface is clean. */
  onConfirmClose: () => void;
  children: ReactNode;
}

export function EditorDrawer({
  dirty,
  busy = false,
  onConfirmClose,
  children,
  ...rest
}: EditorDrawerProps) {
  const { requestClose, confirmDestructive, bypassNext, effectiveDismissDisabled } =
    useUnsavedChangesGuard({ dirty, busy, onConfirmClose });

  const guardValue = useMemo<EditorGuardValue>(
    () => ({ confirmDestructive, bypassNext }),
    [confirmDestructive, bypassNext],
  );

  return (
    <Drawer {...rest} onClose={requestClose} dismissDisabled={effectiveDismissDisabled}>
      <EditorGuardContext.Provider value={guardValue}>{children}</EditorGuardContext.Provider>
    </Drawer>
  );
}
